// ItemDetails.tsx - Updated with includes and excludes support
import React, { useEffect, useState } from 'react';
import {
  FaCalendarAlt,
  FaWater,
  FaHotel,
  FaUtensils,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaChevronRight,
  FaArrowLeft,
  FaStar,
  FaUsers,
  FaClock,
  FaCheckCircle,
  FaList,
  FaExclamationCircle,
  FaTimes,
  FaPlane,
  FaShip,
  FaGlobe,
  FaUmbrellaBeach,
  FaHeart,
  FaTree,
  FaFish,
  FaSwimmingPool,
  FaDumbbell,
  FaSpa,
  FaShieldAlt,
  FaAmbulance,
  FaFireExtinguisher,
  FaPhone,
  FaEnvelope,
  FaPlus,
  FaMinus,
} from 'react-icons/fa';
import { FaLocationDot, FaMoneyBillWave } from 'react-icons/fa6';
import OrderNow from './OrderNow';
import { formatPrice, parsePrice } from '../helpers/parsePrice';

// ============================================================
// TYPES
// ============================================================
interface Location {
  lat: number;
  lng: number;
  name: string;
  description?: string;
}

interface OnImageButton {
  label: string;
  value: string;
  url: string;
  style: 'primary' | 'secondary';
}

interface ItemDetail {
  label: string;
  value: string;
  icon: string;
}

interface ActionButton {
  label: string;
  url: string;
  style: 'primary' | 'secondary';
}

interface Item {
  label: string;
  name: string;
  description?: string;
  image?: string;
  bgImage?: string;
  onImageButtons?: OnImageButton[];
  details: ItemDetail[];
  addOns: any;
  pricing: any;
  includes?: string[];
  excludes?: string[];
  ActionButtons?: ActionButton[];
  rating?: number;
  location?: Location;
  googleMapsUrl?: string;
}

interface ItemDetailsProps {
  item: Item | null;
  isOpen: boolean;
  onClose: () => void;
  bookingEndpoint?: string;
  appData?: any;
}

// ============================================================
// ICON MAPPER
// ============================================================
const ICON_MAP: Record<string, React.ReactNode> = {
  FaCalendarAlt: <FaCalendarAlt className="text-blue-500" />,
  FaWater: <FaWater className="text-cyan-500" />,
  FaHotel: <FaHotel className="text-purple-500" />,
  FaUtensils: <FaUtensils className="text-orange-500" />,
  FaMapMarkerAlt: <FaMapMarkerAlt className="text-red-500" />,
  FaUsers: <FaUsers className="text-green-500" />,
  FaClock: <FaClock className="text-yellow-500" />,
  FaStar: <FaStar className="text-yellow-400" />,
  FaList: <FaList className="text-blue-500" />,
  FaExclamationCircle: <FaExclamationCircle className="text-red-500" />,
  FaPlane: <FaPlane className="text-emerald-500" />,
  FaShip: <FaShip className="text-teal-500" />,
  FaGlobe: <FaGlobe className="text-purple-500" />,
  FaUmbrellaBeach: <FaUmbrellaBeach className="text-yellow-500" />,
  FaHeart: <FaHeart className="text-red-500" />,
  FaTree: <FaTree className="text-green-500" />,
  FaFish: <FaFish className="text-indigo-500" />,
  FaSwimmingPool: <FaSwimmingPool className="text-sky-500" />,
  FaDumbbell: <FaDumbbell className="text-purple-500" />,
  FaSpa: <FaSpa className="text-emerald-500" />,
  FaShieldAlt: <FaShieldAlt className="text-blue-600" />,
  FaAmbulance: <FaAmbulance className="text-red-600" />,
  FaFireExtinguisher: <FaFireExtinguisher className="text-orange-600" />,
  FaPhone: <FaPhone className="text-blue-500" />,
  FaEnvelope: <FaEnvelope className="text-red-500" />,
};

const getIcon = (iconName: string): React.ReactNode => {
  return ICON_MAP[iconName] || <FaInfoCircle className="text-gray-400" />;
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const ItemDetails = ({
  item,
  isOpen,
  onClose,
  bookingEndpoint = '/api/bookings',
  appData,
}: ItemDetailsProps) => {
  // ===== ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS =====
  const [showBooking, setShowBooking] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<OnImageButton | null>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (showBooking) {
          setShowBooking(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, showBooking]);

  if (!isOpen || !item) return null;

  // ============================================================
  // DERIVE PACKAGE INFORMATION
  // ============================================================
  
  const getPackageType = (name: string): string => {
    if (name.toLowerCase().includes('dive')) return 'Dive Package';
    if (name.toLowerCase().includes('stay')) return 'Stay Package';
    if (name.toLowerCase().includes('combo')) return 'Combo Package';
    return 'Package';
  };

  const getDuration = (item: Item): string => {
    const durationDetail = item.details?.find(d => d.label === 'Duration');
    if (durationDetail) return durationDetail.value;
    const match = item.name.match(/(\d+)\s*NIGHTS?/i);
    return match ? `${match[1]} Nights` : 'Custom Duration';
  };

  const pkgInfo = {
    packageType: getPackageType(item.name),
    duration: getDuration(item),
    dives: item.details?.find(d => d.label === 'Dives')?.value || 'Included',
    accommodation: item.details?.find(d => d.label === 'Accommodation')?.value || 'Standard',
    meals: item.details?.find(d => d.label === 'Meals')?.value || 'Breakfast',
    singlePrice: item.onImageButtons?.find(p => p.label === 'Single')?.value || '$0',
    doublePrice: item.onImageButtons?.find(p => p.label === 'Double')?.value || '$0',
  };

// In ItemDetails.tsx - Updated to handle pricing with seasonal and date-specific pricing
const getPackageOptions = () => {
  if (item.pricing) {
    // Build price tiers from pricing object
    const priceTiers = Object.entries(item.pricing.tiers || {}).map(([travellers, price]) => {
      const numTravellers = parseInt(travellers);
      const keys = Object.keys(item.pricing?.tiers || {}).map(Number).sort((a, b) => a - b);
      const currentIndex = keys.indexOf(numTravellers);
      let maxTravellers = numTravellers;
      if (currentIndex < keys.length - 1) {
        maxTravellers = keys[currentIndex + 1] - 1;
      } else {
        maxTravellers = Infinity;
      }
      
      return {
        label: numTravellers === 1 ? 'Single' : `${numTravellers}+ Travellers`,
        value: formatPrice(price, '$'),
        minTravellers: numTravellers,
        maxTravellers: maxTravellers,
      };
    });

    return [{
      value: item.name,
      label: item.label || item.name,
      priceTiers: priceTiers,
      pricing: item.pricing, // Pass the full pricing object
    }];
  }
  
  return [{
    value: item.name,
    label: item.label || item.name,
    price: '$0',
  }];
};

  const getPackageSections = () => {
    const sections = [];

    // Package Details
    const detailItems = item.details?.map(d => ({
      icon: getIcon(d.icon),
      label: d.label,
      value: d.value,
    })) || [];

    if (detailItems.length > 0) {
      sections.push({
        title: 'Package Details',
        icon: <FaInfoCircle className="text-blue-500" />,
        color: 'from-blue-50 to-indigo-50',
        items: detailItems,
        type: 'details',
      });
    }

    // Pricing Options
    if (item.onImageButtons && item.onImageButtons.length > 0) {
      sections.push({
        title: 'Pricing Options',
        icon: <FaMoneyBillWave className="text-green-500" />,
        color: 'from-green-50 to-emerald-50',
        items: item.onImageButtons.map(btn => ({
          icon: btn.style === 'primary' ? <FaUsers className="text-blue-500" /> : <FaUsers className="text-purple-500" />,
          label: btn.label,
          value: btn.value,
        })),
        type: 'pricing',
      });
    }

    return sections;
  };

  // Handle booking
  const handleBookNow = (btn?: OnImageButton) => {
    if (btn) {
      setSelectedPackage(btn);
    } else if (item.onImageButtons && item.onImageButtons.length > 0) {
      setSelectedPackage(item.onImageButtons[0]);
    }
    setShowBooking(true);
  };

  const handleBookingSuccess = (data: any, response: any) => {
    console.log('Booking successful:', data, response);
  };

  const handleBookingError = (error: Error) => {
    console.error('Booking failed:', error);
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="fixed inset-0 z-[9999] bg-white overflow-y-auto">
      {/* ===== HEADER ===== */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => {
                if (showBooking) {
                  setShowBooking(false);
                } else {
                  onClose();
                }
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium"
            >
              <FaArrowLeft className="text-lg" />
              <span className="hidden sm:inline">
                {showBooking ? 'Back to Details' : 'Back'}
              </span>
            </button>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-semibold text-gray-900 truncate">
                {showBooking ? 'Complete Your Booking' : (item.label || item.name)}
              </h1>
            </div>

            <div className="w-20 sm:w-32"></div>
          </div>
        </div>
      </div>

      {/* ===== CONTENT ===== */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {showBooking ? (
          // ===== BOOKING FORM =====
          <div className="animate-fade-in">
            <OrderNow
              endpoint={bookingEndpoint}
              title={`Book ${item.label || item.name}`}
              subtitle={`${pkgInfo.packageType} - ${pkgInfo.duration}`}
              submitLabel="Complete Booking"
              trigger="inline"
              bookingType="package"
              packageOptions={getPackageOptions()}
              addOns={item.addOns || []}
              showAddOns={true}
              taxRate={0.12} // 12% tax
              serviceFeeRate={0.05} // 5% service fee
              currencySymbol="$"
              showPriceBreakdown={true}
              showTravellers={true}
              requireTravellers={true}
              showTravellingDate={true}
              requireTravellingDate={true}
              showPackage={true}
              requirePackage={true}
              showNotes={true}
              persistData={true}
              onSuccess={handleBookingSuccess}
              onError={handleBookingError}
              className="bg-white rounded-2xl"
              buttonClassName="bg-gradient-to-r from-blue-600 to-indigo-600"
              inputClassName="bg-white"
            />
          </div>
        ) : (
          // ===== DETAILS VIEW =====
          <>
            {/* Hero Image */}
            <div className="relative h-80 rounded-2xl overflow-hidden mb-6">
              <img
                src={item.bgImage || item.image}
                alt={item.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 bg-blue-500/80 backdrop-blur-sm text-white text-sm font-semibold rounded-full">
                    {pkgInfo.packageType}
                  </span>
                  <span className="px-3 py-1 bg-green-500/80 backdrop-blur-sm text-white text-sm font-semibold rounded-full">
                    {pkgInfo.duration}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {item.label || item.name}
                </h2>
                {item.rating && (
                  <div className="flex items-center gap-2 text-white/90">
                    <FaStar className="text-yellow-400" />
                    <span className="font-semibold">{item.rating}</span>
                    <span className="text-white/60">•</span>
                    <span className="text-white/70">Popular Package</span>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {item.description && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <FaInfoCircle className="text-blue-500" />
                  About This Package
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            )}

            {/* Package Sections */}
            {getPackageSections().map((section, idx) => (
              <div key={idx} className={`mb-6 bg-gradient-to-r ${section.color} rounded-2xl p-6`}>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  {section.icon}
                  {section.title}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {section.items.map((item, i) => (
                    <div key={i} className="bg-white/60 backdrop-blur-sm rounded-xl p-4 text-center">
                      <div className="text-2xl mb-2 flex justify-center">{item.icon}</div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">{item.label}</p>
                      <p className="text-sm font-semibold text-gray-800 mt-1">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* ===== INCLUDES SECTION ===== */}
            {item.includes && item.includes.length > 0 && (
              <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FaPlus className="text-green-500" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {item.includes.map((include, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700">
                      <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                      <span>{include}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ===== EXCLUDES SECTION ===== */}
            {item.excludes && item.excludes.length > 0 && (
              <div className="mb-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FaMinus className="text-red-500" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {item.excludes.map((exclude, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700">
                      <FaTimes className="text-red-500 mt-1 flex-shrink-0" />
                      <span>{exclude}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ===== ACTION BUTTONS SECTION ===== */}
            <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
              {item.ActionButtons && item.ActionButtons.map((btn, i) => (
                <button
                  key={i}
                  onClick={() => {
                    if (btn.label.toLowerCase().includes('book')) {
                      handleBookNow();
                    } else {
                      window.location.href = btn.url;
                    }
                  }}
                  className={`flex-1 min-w-[150px] text-center px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                    btn.style === 'primary'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:scale-[1.02]'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                  }`}
                >
                  <span className="flex items-center justify-center gap-2">
                    {btn.label.toLowerCase().includes('book') && (
                      <FaStar className="text-yellow-300" />
                    )}
                    {btn.label}
                    {btn.label.toLowerCase().includes('book') && (
                      <FaChevronRight className="text-sm" />
                    )}
                  </span>
                </button>
              ))}

              {(!item.ActionButtons || item.ActionButtons.length === 0) && (
                <button
                  onClick={() => handleBookNow()}
                  className="flex-1 min-w-[150px] text-center px-6 py-3 rounded-xl font-semibold transition-all duration-300 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:scale-[1.02]"
                >
                  <span className="flex items-center justify-center gap-2">
                    <FaStar className="text-yellow-300" />
                    Book Now
                    <FaChevronRight className="text-sm" />
                  </span>
                </button>
              )}
            </div>

            {/* Location */}
            {item.location && (
              <div className="mb-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <FaLocationDot className="text-red-500" />
                  Location
                </h3>
                <div className="bg-blue-50 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FaMapMarkerAlt className="text-blue-500 text-xl" />
                    <div>
                      <p className="font-medium text-gray-800">{item.location.name}</p>
                      <p className="text-sm text-gray-500">
                        {item.location.lat?.toFixed(4)}°N, {item.location.lng?.toFixed(4)}°E
                      </p>
                    </div>
                  </div>
                  {item.googleMapsUrl && (
                    <a
                      href={item.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium text-sm"
                    >
                      View on Map
                      <FaChevronRight className="text-xs" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default ItemDetails;